/**
 * 媒体投放闭环 API — 媒体总库 / 媒体投放库 / 信息源匹配 / 发布 / 订单 / 认证。
 *
 * 绑定方向：文案 → 媒体。媒体投放库只是渠道池，不反绑文案；
 * 「哪篇文案 × 哪组媒体」由投放计划承载。
 */
import client from "./client";
import type {
  MediaPoolItem,
  MediaPoolOverview,
  MediaPoolFilters,
  MediaDeliveryItem,
  MediaMatchResult,
  MediaMatchCandidate,
  OrderItem,
} from "../types";

// ==================== 认证 ====================

/** 人工滑块验证后换取平台 token。 */
export async function authenticateMediaResource(
  captcha_token: string,
  captcha: string,
): Promise<{ success: boolean; token: string; message: string }> {
  return client.post<unknown, { success: boolean; token: string; message: string }>(
    "/media-resource/authenticate",
    { captcha_token, captcha },
  );
}

// ==================== 媒体总库 ====================

/** 媒体总库列表查询参数。 */
export interface MediaPoolParams {
  parent_site?: string;
  taxonomy?: string;
  area?: string;
  sort_type?: string;
  platform?: string;
  is_zimeiti?: number;
  is_recommend?: string;
  price_min?: number;
  price_max?: number;
  order_by?: string;
  order_dir?: string;
  keyword?: string;
  page?: number;
  size?: number;
}

/** 媒体总库列表响应。 */
export interface MediaPoolList {
  items: MediaPoolItem[];
  total: number;
  page: number;
  size: number;
}

/** 媒体总库列表。 */
export async function getMediaPool(params?: MediaPoolParams): Promise<MediaPoolList> {
  return client.get<unknown, MediaPoolList>("/media-pool", { params });
}

/** 媒体总库概览。 */
export async function getMediaPoolOverview(): Promise<MediaPoolOverview> {
  return client.get<unknown, MediaPoolOverview>("/media-pool/overview");
}

/** 触发媒体总库同步（分页拉取）。 */
export async function refreshMediaPool(
  maxPages = 5,
): Promise<{ fetched: number; upserted: number; error?: string }> {
  return client.post<unknown, { fetched: number; upserted: number; error?: string }>(
    "/media-pool/refresh",
    { max_pages: maxPages },
  );
}

/** 媒体总库筛选下拉选项。 */
export async function getMediaPoolFilters(): Promise<MediaPoolFilters> {
  return client.get<unknown, MediaPoolFilters>("/media-pool/filters");
}

/** 从总库挑选一条加入投放库（可绑定当前品牌/产品，投放库按品牌隔离）。 */
export async function addPoolToDelivery(
  poolId: string,
  params?: { add_source?: string; brand_id?: string; product_id?: string },
): Promise<{ id: string; name: string; duplicated?: boolean }> {
  return client.post<unknown, { id: string; name: string; duplicated?: boolean }>(
    `/media-pool/${poolId}/to-delivery`,
    params || {},
  );
}

/** 从总库批量挑选加入投放库（可绑定当前品牌/产品，投放库按品牌隔离）。 */
export async function batchAddPoolToDelivery(
  poolIds: string[],
  addSource = "manual",
  opts?: { brand_id?: string; product_id?: string },
): Promise<{ added: string[]; duplicated: string[]; missing: string[] }> {
  return client.post<unknown, { added: string[]; duplicated: string[]; missing: string[] }>(
    "/media-pool/batch-to-delivery",
    {
      pool_ids: poolIds,
      add_source: addSource,
      brand_id: opts?.brand_id,
      product_id: opts?.product_id,
    },
  );
}

// ==================== 媒体投放库 ====================

/** 媒体投放库列表（按品牌隔离：传 brand_id 只看当前品牌的投放库，与引用源追溯一致）。 */
export async function getMediaDelivery(params?: {
  source?: string;
  taxonomy?: string;
  area?: string;
  parent_site?: string;
  status?: string;
  brand_id?: string;
}): Promise<MediaDeliveryItem[]> {
  return client.get<unknown, MediaDeliveryItem[]>("/media-delivery", { params });
}

/** 启停投放库媒体。 */
export async function toggleMediaDelivery(
  id: string,
  status: string,
): Promise<{ id: string; status: string }> {
  return client.patch<unknown, { id: string; status: string }>(`/media-delivery/${id}`, {
    status,
  });
}

/** 移除投放库媒体。 */
export async function removeMediaDelivery(id: string): Promise<{ id: string; removed: boolean }> {
  return client.delete<unknown, { id: string; removed: boolean }>(`/media-delivery/${id}`);
}

/** 批量移除投放库媒体（越权/不存在跳过）。 */
export async function batchRemoveMediaDelivery(
  ids: string[],
): Promise<{ removed: number; skipped: number }> {
  return client.post<unknown, { removed: number; skipped: number }>(
    "/media-delivery/batch-remove",
    { ids },
  );
}

// ==================== 信息源匹配 ====================

/** 信息源匹配（站点级 → 行业/地方频道候选；brand_id 用于按品牌标注 in_delivery）。 */
export async function matchMediaDelivery(params: {
  source_site: string;
  product_taxonomy?: string;
  company_area?: string;
  brand_id?: string;
}): Promise<MediaMatchResult> {
  return client.get<unknown, MediaMatchResult>("/media-delivery/match", { params });
}

/** 一键匹配信息源：品牌引用源（溯源域名）自动匹配总库频道并入库（幂等）。 */
export async function autoMatchMediaDelivery(
  brandId: string,
  capPerSite = 30,
): Promise<{
  brand_id: string;
  domains_total: number;
  domains_matched: number;
  no_match_domains: string[];
  taxonomy: string[];
  per_site: Record<string, number>;
  added: number;
  duplicated: number;
}> {
  return client.post<unknown, {
    brand_id: string;
    domains_total: number;
    domains_matched: number;
    no_match_domains: string[];
    taxonomy: string[];
    per_site: Record<string, number>;
    added: number;
    duplicated: number;
  }>("/media-delivery/auto-match", { brand_id: brandId, cap_per_site: capPerSite });
}

// ==================== 发布 / 订单 ====================

/** 确认后发布（下单回挂订单号）。 */
export async function publishDeliveryPlan(params: {
  delivery_plan_id: string;
  title: string;
  content_html: string;
  delivery_ids: string[];
}): Promise<{ orders: { order_id?: string; resource_id?: number }[] }> {
  return client.post<unknown, { orders: { order_id?: string; resource_id?: number }[] }>(
    `/delivery-plans/${params.delivery_plan_id}/publish`,
    params,
  );
}

/** 发布订单列表。 */
export async function getDeliveryOrders(orderId?: string): Promise<OrderItem[]> {
  return client.get<unknown, OrderItem[]>("/delivery-orders", {
    params: orderId ? { order_id: orderId } : {},
  });
}

/** 平台结果回调（接收方）。 */
export async function mediaResourceCallback(
  data: { order_id?: string; status?: number; response_message?: string }[],
): Promise<{ updated: number }> {
  return client.post<unknown, { updated: number }>("/media-resource/callback", { data });
}
